import React from 'react';
import { countries } from '@local/finsera-core/src/data';
import { useEffectWithoutFirst } from '@local/finsera-core/src/hooks';
import { mui } from '@local/finsera-core/src/libs';
import * as types from '@local/finsera-core/src/types';

import { getConstraintType } from '../../optimizer-utils/constraint-type';
import Bounds from '../bounds';
import SharedConstraintFields from '../shared-fields';
import Target from '../target';

type CountryConstraintProps = {
  id: string;
  params: types.optimizer.CountryDraft;
  setParams: (_params: types.optimizer.CountryDraft) => void;
  universe: types.universe.Universe;
  readOnly: boolean;
  validationDef: types.common.ValidationErrors;
  optimizerType: 'basket' | 'backtest';
};

const CountryConstraint = ({
  id,
  params,
  setParams,
  readOnly,
  universe,
  optimizerType,
  validationDef,
}: CountryConstraintProps) => {
  const [showMore, setShowMore] = React.useState(false);
  const [constraint, setConstraint] = React.useState(params);

  const universeCountries = universe.definition?.quotation_countries;

  const filteredCountries = countries.filter((c) => universeCountries.includes(c.code));

  React.useEffect(() => {
    if (optimizerType == 'basket' && params.benchmark_relative == null) {
      setConstraint({ ...constraint, benchmark_relative: true });
    }
  }, []);

  useEffectWithoutFirst(() => {
    setParams(constraint);
  }, [constraint]);

  const form = () => {
    if (getConstraintType(constraint) == 'BOUNDS') {
      if (showMore) {
        return (
          <Bounds
            id={id}
            params={constraint}
            setParams={setConstraint}
            isPercent
            readOnly={readOnly}
            validationDef={validationDef}
          >
            <SharedConstraintFields
              id={id}
              params={constraint}
              setParams={setConstraint}
              readOnly={readOnly}
              validationDef={validationDef}
            />
          </Bounds>
        );
      }

      return (
        <Bounds
          id={id}
          params={constraint}
          setParams={setConstraint}
          isPercent
          readOnly={readOnly}
          validationDef={validationDef}
        />
      );
    }

    if (showMore) {
      return (
        <Target params={constraint} setParams={setConstraint} readOnly={readOnly}>
          <SharedConstraintFields
            id={id}
            params={constraint}
            setParams={setConstraint}
            readOnly={readOnly}
            validationDef={validationDef}
          />
        </Target>
      );
    }

    return <Target params={constraint} setParams={setConstraint} readOnly={readOnly} />;
  };

  const countriesValue = React.useMemo(() => {
    return ((params.value as string[]) || []).map((c) => countries.find((cf) => cf.code == c));
  }, [params.value, countries]);

  return (
    <mui.core.Box p={3}>
      {/* {!readOnly && (
        <ui.Button
          sx={uiStyles.fullCardButton}
          color="primary"
          size="small"
          fullWidth
          onClick={() => setConstraint({ 
            type: constraint.type == 'BOUNDS' ? 'TARGET' : 'BOUNDS',
            target_penalty: constraint.type == 'BOUNDS' ? 1.0 : undefined, 
          })}
        >
          { constraint.type == 'BOUNDS' ? 'Change to target mode' : 'Change to bounds mode' }
        </ui.Button>
      )} */}

      <mui.core.Grid container spacing={4}>
        <mui.core.Grid size={{ xs: 12, md: 3 }} >
          <mui.core.Box px={3}>
            <mui.core.FormControlLabel
              control={
                <mui.core.Switch
                  size="small"
                  checked={constraint.benchmark_relative}
                  onChange={() =>
                    setConstraint({
                      ...constraint,
                      benchmark_relative: !constraint.benchmark_relative,
                    })
                  }
                  disabled={readOnly}
                />
              }
              label={optimizerType == 'backtest' ? 'Benchmark Relative' : 'Relative to Target Weights'}
            />
          </mui.core.Box>
        </mui.core.Grid>
        <mui.core.Grid size={{ xs: 12, md: 9, xl: 10 }}>
          <mui.core.Autocomplete
            multiple
            disabled={readOnly}
            options={filteredCountries}
            groupBy={(c) => c.continent}
            size="small"
            value={countriesValue}
            onChange={(e, val) => {
              setConstraint({
                ...constraint,
                value: val.map((c) => (c as types.country.Country).code),
              });
            }}
            getOptionLabel={(option) =>
              `${(option as types.country.Country).name} (${(option as types.country.Country).code})`
            }
            renderInput={(params) => (
              <mui.core.TextField
                {...params}
                fullWidth
                label="Country"
                variant="outlined"
                required
                size="small"
                error={validationDef?.fields?.includes(`country_${id}`)}
              />
            )}
            renderValue={(tagValue, getTagProps) =>
              tagValue.map((option, index) => (
                <mui.core.Chip key={option.code} label={option.code} {...getTagProps({ index })} />
              ))
            }
          />
        </mui.core.Grid>
      </mui.core.Grid>

      <mui.core.Box p={3}>
        {form()}
        <mui.core.Button
          onClick={() => {
            setShowMore(!showMore);
          }}
          color="primary"
          sx={{ mt: 3 }}
          startIcon={showMore ? <mui.icons.Remove /> : <mui.icons.Add />}
        >
          {showMore ? <>Show Less</> : <>Show More</>}
        </mui.core.Button>
      </mui.core.Box>
    </mui.core.Box>
  );
};

export default CountryConstraint;
